import { ROLES } from '../constants';
import { EmployeeInfo } from '../constants/informationText';
import { ASSET_TYPE_FIELDS } from './assetType';
import { checkIfUserIsSimpleUser } from '../helper/roles';
import { categoryFieldName } from 'utils/helper/product';
import API from 'services/api';
import { setSiteName } from 'utils/helper/site';
import isEmpty from 'lodash/isEmpty';
import { transform as siteListTransform } from 'utils/DataTransform/Sites/SitesList';
import { SiteInfo } from 'utils/constants/informationText';

export const getMultiRoleField = ({ role, serviceRoleMap, toggleSize }) => {
  const availableRoles = serviceRoleMap.map(({ role }) => role);

  const [filteredRole] = serviceRoleMap.filter(
    ({ role: foundRole }) => foundRole.id === role,
  );
  const [hasMultiRole] = availableRoles.filter(f =>
    filteredRole?.multiRole?.includes(f.id),
  );

  return hasMultiRole
    ? [USER_FIELDS[hasMultiRole.id]({ size: toggleSize })]
    : null;
};

export const USER_FIELDS = {
  employeeId: ({
    placeholder = 'Enter unique employee id. e.g. EMP-0001',
    size = 'half',
    role = '',
  } = {}) => ({
    field: 'employeeId',
    label: 'Employee ID',
    type: 'text',
    required: checkIfUserIsSimpleUser(role),
    size,
    placeholder: placeholder,
    info: EmployeeInfo.employeeId,
  }),
  role: ({ availableRoles, ...props }) => {
    return {
      field: 'role',
      type: 'select',
      required: true,
      size: 'half',
      options: availableRoles,
      ...props,
    };
  },
  departments: ({ userAssignedDepartments, disabled = false, ...props }) => {
    return {
      field: 'departments',
      type: 'select',
      required: false,
      multiple: true,
      size: 'half',
      options: userAssignedDepartments,
      disabled,
      ...props,
    };
  },
  firstName: {
    field: 'firstName',
    type: 'text',
    required: true,
    size: 'half',
    placeholder: 'Enter first name',
  },

  lastName: {
    field: 'lastName',
    type: 'text',
    required: true,
    size: 'half',
    placeholder: 'Enter last name',
  },

  phone: countries => ({
    field: 'phone',
    type: 'phone',
    required: false,
    size: 'half',
    secondaryOptions: countries,
  }),

  email: editMode => ({
    field: 'email',
    type: 'text',
    required: true,
    size: 'half',
    placeholder: 'Enter unique email',
    info: EmployeeInfo.email,
    disabled: editMode,
  }),

  passcode: editMode => ({
    field: 'passcode',
    type: 'text',
    size: 'half',
    required: !editMode,
    placeholder: 'Enter 4-6 digit passcode. e.g. 4603',
    info: EmployeeInfo.passCode,
  }),

  assignedSite: ({
    selectedDepartments,
    securityCompanyId,
    userAssignedDepartments,
    ...props
  }) => ({
    field: 'assignedSite',
    type: 'async-select',
    required: false,

    nullable: true,
    info: SiteInfo.businessClient,
    size: 'half',
    valueType: 'object',
    fetchOptions: API.fetchSitesList,
    fetchFilters: {
      securityCompanyId,
      archived: false,
      ...(!isEmpty(selectedDepartments) && {
        departmentId: selectedDepartments
          .map(m => ({ id: m.id }))
          .map(m => m.id),
      }),
    },
    optionLabelKey: 'name',
    resetWhen: [
      {
        value: selectedDepartments,
        condition: val => {
          return val;
        },
      },
    ],
    dataTransform: ({ data }) => {
      const transformedNameSites = data.map(site => ({
        ...site,
        id: site.id,
        name: setSiteName(site),
      }));
      return siteListTransform(transformedNameSites, userAssignedDepartments);
    },
    ...props,
    // info: 'A guard must be assigned to a site to perform patrols there.',
  }),

  isTeamLead: {
    field: 'isTeamLead',
    label: 'Is Team Lead',
    type: 'toggle',
    size: 'half',
    info: EmployeeInfo.isTeamLead,
  },

  services: ({ services, ...rest }) => {
    if (!services || (Array.isArray(services) && services.length < 1)) {
      return [
        {
          field: '',
          label: '',
          type: 'checkbox-options',
          options: [],
          noOptionsText: 'No available services for this role',
        },
      ];
    }

    return services.map(item => {
      const { allowableServices, category } = item;
      return {
        field: category ? categoryFieldName(category) : 'services',
        label: category ? categoryFieldName(category) : 'Services',
        type: 'checkbox-options',
        options: allowableServices,
        noOptionsText: 'No available services for this role',
        denseFormGroup: true,
        ...rest,
      };
    });
  },

  // ROLE SPECIFIC FIELDS
  [ROLES.RESPONDER.id]: ({ size = 'half' } = {}) => ({
    id: ROLES.RESPONDER.id,
    field: 'isResponder',
    type: 'toggle',
    required: true,
    size: size || 'half',
    info: EmployeeInfo.isResponder,
  }),

  // DETAIL SECTION
  qualifications: allowableCategories => ({
    field: 'qualifications',
    label: 'Qualifications',
    type: 'sub-custom-form',
    info: EmployeeInfo.qualifications,
    formHeader: 'Add new qualification',
    subFields: [
      ASSET_TYPE_FIELDS.category({ allowableCategories }),
      {
        field: 'typeName',
        type: 'text',
      },
      {
        field: 'validFrom',
        type: 'date',
        size: 'quarter',
      },
      {
        field: 'validTo',
        type: 'date',
        size: 'quarter',
      },
    ],
    defaultValues: {
      category: '',
      typeName: '',
      validFrom: '',
      validTo: undefined,
    },
    requiredFields: ['category', 'typeName', 'validFrom'],
  }),

  languages: languages => ({
    field: 'languages',
    type: 'multiselect',
    options: languages,
    info: EmployeeInfo.languages,
  }),

  notes: { field: 'notes', type: 'textArea', maxLength: 250 },

  // misc
  hidden: {
    field: 'hiddenField',
    type: 'toggle',
    size: 'half',
    visibility: 'hidden',
  },
  businessClient: ({
    securityCompanyId,
    archived,
    multiple = false,
    disabled = false,
  } = {}) => ({
    label: 'Business Client',
    field: 'businessClient',
    type: 'async-select',
    size: 'half',
    valueType: 'object',
    multiple,
    disabled,
    useAsyncInFilters: true, // if you are using async-select in filters make sure this is set to true
    fetchOptions: API.fetchBusinessClientList,
    fetchFilters: {
      securityCompanyId,
      archived,
    },
    optionLabelKey: 'name',
    dataTransform: ({ data }) => {
      return data;
    },
  }),
};

export const getDefaultUserDetailsSection = ({
  availableServices,
  subscribedProducts,
  languages,
}) => {
  return [
    ...USER_FIELDS.services({ services: availableServices }),
    USER_FIELDS.qualifications(subscribedProducts),
    USER_FIELDS.languages(languages),
    USER_FIELDS.notes,
  ];
};
